import React, { useState, useRef, useEffect } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Plus, Trash2, Check, Sparkles, Volume2 } from 'lucide-react';
import { PlatformLogo, PlatformId } from '../components/PlatformIcons';
import { Question } from '../types';
import { useTheme } from '../lib/ThemeContext';
import VoiceRecorder from '../components/VoiceRecorder';
import QuestionBuddy from '../components/QuestionBuddy';
import AuthGateModal from '../components/AuthGateModal';
import { savePendingTest, getPendingTest } from '../lib/pendingTest';
import { notifyAdminOfPendingSession } from '../lib/adminAlert';
import { UNIQUE_CODES } from '../lib/countryCodes';
import { formatPhoneNumber } from '../lib/phone';
import { track } from '../lib/analytics';
import { unlockOpenedForSession } from '../lib/referral';
import { buildWhatsAppTeaser } from '../lib/dispatchMessages';

interface CreateTestProps { user: User | null; }

// ── NANP area codes (+1 is shared by the US and Canada; the area code is what
// actually tells them apart) ────────────────────────────────────────────────
const CANADA_AREA_CODES = new Set([
  '204', '226', '236', '249', '250', '289', '306', '343', '354', '365', '367',
  '368', '382', '403', '416', '418', '431', '437', '438', '450', '468', '474',
  '506', '514', '519', '548', '579', '581', '584', '587', '604', '613', '639',
  '647', '672', '683', '705', '709', '742', '753', '778', '780', '782', '807',
  '819', '825', '867', '873', '879', '902', '905',
]);

// +7 is shared by Russia and Kazakhstan. Under their 2025 CIS numbering
// agreement, Kazakhstan was assigned the 000–099, 600–699, and 700–799
// blocks — so a leading '0', '6', or '7' after +7 means Kazakhstan.
const isKazakhstanArea = (area: string) => /^[067]/.test(area);

const PLATFORMS: { name: PlatformId; hint: string }[] = [
  { name: 'WhatsApp', hint: 'Sends right away' },
  { name: 'Snapchat', hint: 'Username' },
  { name: 'Telegram', hint: 'Username' },
  { name: 'Instagram', hint: 'Username' },
  { name: 'TikTok', hint: 'Username' },
  { name: 'SMS', hint: 'Phone number' },
];

const PHONE_PLATFORMS = ['WhatsApp', 'SMS'];

function buildContact(platform: string, iso: string, number: string): { contact: string; valid: boolean } {
  if (!PHONE_PLATFORMS.includes(platform)) return { contact: number, valid: !!number };
  // Real phone-numbering-plan validation instead of naive "code + digits"
  // concatenation — this is what catches (and fixes) someone re-typing the
  // country code themselves, or leaving a leading 0 in a local number.
  const { e164, valid } = formatPhoneNumber(number, iso);
  return { contact: e164 ?? '', valid };
}

function shouldSendInstantly(platform: string) {
  return platform === 'WhatsApp';
}

export default function CreateTest({ user }: CreateTestProps) {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { theme } = useTheme();
  const light = theme === 'light';

  const [partner_name, setPartnerName] = useState('');
  const [rawNumber, setRawNumber] = useState('');
  const [partner_contact, setPartnerContact] = useState(''); // handle/username for non-phone platforms
  const [partner_platform, setPartnerPlatform] = useState<PlatformId>('WhatsApp');
  const platformRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Arrow keys move between platforms and select as they go — the expected
  // behaviour for a radio group, and what screen readers announce.
  const onPlatformKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(e.key)) return;
    e.preventDefault();
    const current = PLATFORMS.findIndex(p => p.name === partner_platform);
    const last = PLATFORMS.length - 1;
    let next = current;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = current === last ? 0 : current + 1;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = current === 0 ? last : current - 1;
    if (e.key === 'Home') next = 0;
    if (e.key === 'End') next = last;
    setPartnerPlatform(PLATFORMS[next].name);
    platformRefs.current[next]?.focus();
  };
  const [countryIso, setCountryIso] = useState('GH'); // default Ghana
  const [showCodePicker, setShowCodePicker] = useState(false);
  const [codeSearch, setCodeSearch] = useState('');
  const [questions, setQuestions] = useState<Question[]>([
    { id: '1', text: '', expectedAnswer: '' },
  ]);
  const [liveAiEnabled, setLiveAiEnabled] = useState(() => new URLSearchParams(search).get('liveAi') === '1');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAuthGate, setShowAuthGate] = useState(false);
  // Which question card opened the Buddy assistant (and whether straight into voice mode).
  const [buddyFor, setBuddyFor] = useState<{ id: string; mode: 'chat' | 'voice' } | null>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLInputElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, maxListHeight: 220, openUp: false });

  // If a signed-out visitor already filled this form in once and got sent
  // to sign up, restore what they typed so they never have to redo it.
  useEffect(() => {
    const draft = getPendingTest();
    if (!draft) return;
    setPartnerName(draft.partner_name);
    setPartnerPlatform(draft.partner_platform as PlatformId);
    if (PHONE_PLATFORMS.includes(draft.partner_platform)) {
      setRawNumber(draft.partner_contact.replace(/^\+?\d+\s*/, '').trim() || draft.partner_contact);
    } else {
      setPartnerContact(draft.partner_contact);
    }
    if (draft.questions?.length) setQuestions(draft.questions);
    if (draft.live_ai_enabled) setLiveAiEnabled(true);
  }, []);

  const isPhonePlatform = PHONE_PLATFORMS.includes(partner_platform);
  const selectedCountry = UNIQUE_CODES.find(c => c.iso === countryIso) ?? UNIQUE_CODES[0];
  const filteredCodes = (() => {
    const q = codeSearch.trim().toLowerCase();
    if (!q) return UNIQUE_CODES;
    const matches = UNIQUE_CODES.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.code.includes(q) ||
      c.iso.toLowerCase().includes(q) ||
      c.aliases.some(a => a.toLowerCase().includes(q))
    );
    // Rank exact/prefix matches (e.g. "us" -> United States) above loose substring matches
    const score = (c: typeof UNIQUE_CODES[number]) => {
      const name = c.name.toLowerCase();
      if (c.iso.toLowerCase() === q || c.aliases.some(a => a.toLowerCase() === q)) return 0;
      if (name === q) return 1;
      if (name.startsWith(q)) return 2;
      if (c.aliases.some(a => a.toLowerCase().startsWith(q))) return 3;
      if (c.code === q || c.code === `+${q}`) return 4;
      return 5;
    };
    return [...matches].sort((a, b) => score(a) - score(b));
  })();

  // Auto-detect the real country from the number itself, for dial codes that
  // are shared by more than one country in the list. Both share the same
  // dial code, so the digits themselves are the only real signal.
  useEffect(() => {
    const digits = rawNumber.replace(/\D/g, '');
    if (digits.length < 3) return; // wait until the area code is fully typed
    const area = digits.slice(0, 3);

    if (selectedCountry.code === '+1') {
      // US vs Canada
      const isCanadianArea = CANADA_AREA_CODES.has(area);
      if (isCanadianArea && countryIso !== 'CA') setCountryIso('CA');
      else if (!isCanadianArea && countryIso !== 'US') setCountryIso('US');
    } else if (selectedCountry.code === '+7') {
      // Kazakhstan vs Russia
      const isKzArea = isKazakhstanArea(area);
      if (isKzArea && countryIso !== 'KZ') setCountryIso('KZ');
      else if (!isKzArea && countryIso !== 'RU') setCountryIso('RU');
    }
    // Note: +590 (Guadeloupe / Saint Barthélemy / Saint Martin) genuinely
    // shares the same number blocks with no reliable digit-based way to
    // tell them apart, so it isn't auto-detected — pick it manually.
  }, [rawNumber, selectedCountry.code, countryIso]);

  // Close picker when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setShowCodePicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const addQuestion = () => setQuestions([...questions, { id: Math.random().toString(36).substr(2, 9), text: '', expectedAnswer: '' }]);
  const removeQuestion = (id: string) => { if (questions.length > 1) setQuestions(questions.filter(q => q.id !== id)); };
  const updateQuestion = (id: string, text: string) => setQuestions(questions.map(q => q.id === id ? { ...q, text } : q));
  const updateExpected = (id: string, expectedAnswer: string) => setQuestions(questions.map(q => q.id === id ? { ...q, expectedAnswer } : q));
  const updateQuestionAudio = (id: string, audioUrl: string) => setQuestions(questions.map(q => q.id === id ? { ...q, audioUrl } : q));
  const clearQuestionAudio = (id: string) => setQuestions(questions.map(q => q.id === id ? { ...q, audioUrl: undefined } : q));

  const getContact = (): { contact: string; valid: boolean } =>
    isPhonePlatform ? buildContact(partner_platform, countryIso, rawNumber) : { contact: partner_contact, valid: !!partner_contact };

  const handleSubmit = async () => {
    const { contact, valid: contactValid } = getContact();
    if (!partner_name || !contact || questions.some(q => !q.text && !q.audioUrl)) { alert('Please fill in all fields (or record a voice question)'); return; }
    if (isPhonePlatform && !rawNumber) { alert('Please enter a phone number'); return; }
    if (isPhonePlatform && !contactValid) {
      alert(`That doesn't look like a valid ${selectedCountry.name} phone number. Just type the local number — the ${selectedCountry.code} is already added for you, so don't type it again.`);
      return;
    }

    if (!user) {
      // Save the draft and open sign-up right here — no navigating away,
      // no re-typing anything. The moment they have a session, the app-level
      // auto-submit effect sends this exact draft and takes them to their dashboard.
      savePendingTest({ partner_name, partner_contact: contact, partner_platform, questions, live_ai_enabled: liveAiEnabled });
      setShowAuthGate(true);
      return;
    }

    setIsSubmitting(true);
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (!session || sessionError) { await supabase.auth.signOut(); alert('Session expired. Please sign in again.'); return; }

      const instant = shouldSendInstantly(partner_platform);
      const insertPayload = {
        creator_id: user.id, creator_email: user.email, partner_name,
        partner_contact: contact, partner_platform, has_hints: false,
        status: instant ? 'dispatched' : 'pending', questions,
        ...(liveAiEnabled ? { live_ai_enabled: true } : {}),
      };
      const { data, error } = await supabase.from('tests').insert([{
        ...insertPayload,
      }]).select().single();

      if (error) throw error;

      // Everything after the insert runs in the background — the session
      // already exists, so don't make the user wait on the network (or on
      // WhatsApp) before taking them to their dashboard. send-message handles
      // its own failures server-side (falls back to the admin queue).
      void unlockOpenedForSession(data.id);

      if (instant) {
        void supabase.functions.invoke('send-message', {
          body: { to: contact, name: partner_name, link: `https://yhuu.life/test/${data.id}`, channel: partner_platform, teaser: buildWhatsAppTeaser(questions[0]?.text) },
        }).catch(err => console.error('send-message failed:', err));
      } else {
        // Not auto-sent — it's now waiting in the admin queue. Ping the operator.
        notifyAdminOfPendingSession(data.id);
      }

      track('session_sent', { platform: partner_platform, questions: questions.length, instant }, data.id);
      navigate(`/dashboard?newTest=${data.id}`);
    } catch (error) {
      console.error('Error creating test:', error);
      alert("Couldn't create your session. Ensure your Supabase tables are set up correctly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Theme tokens ─────────────────────────────────────────────────────────────
  const t = {
    pageBg:       light ? '#fdf4f7' : '#09090b',
    cardBg:       light ? '#ffffff' : '#111115',
    cardBorder:   light ? '#f0d6e0' : '#1e1e24',
    inputBg:      light ? '#fff0f5' : '#09090b',
    inputBorder:  light ? '#f0d6e0' : '#27272a',
    inputColor:   light ? '#1a0a0f' : '#ffffff',
    labelColor:   light ? '#9d6b7a' : '#52525b',
    subColor:     light ? '#9d6b7a' : '#3f3f46',
    mutedColor:   light ? '#c9a0b0' : '#71717a',
    platBg:       light ? '#ffffff' : '#111115',
    platBorder:   light ? '#f0d6e0' : '#1e1e24',
    platActiveBg: light ? '#fff0f5' : '#1c0a0a',
    qCardBg:      light ? '#ffffff' : '#111115',
    qCardBorder:  light ? '#f0d6e0' : '#1e1e24',
    dividerColor: light ? '#f0d6e0' : '#1e1e24',
    addBtnBg:     light ? '#fff0f5' : '#1c0a0a',
    addBtnBorder: light ? '#fca5a5' : '#7f1d1d',
    titleColor:   light ? '#1a0a0f' : '#ffffff',
    pickerBg:     light ? '#ffffff' : '#18181b',
    pickerBorder: light ? '#f0d6e0' : '#27272a',
    pickerHover:  light ? '#fff0f5' : '#27272a',
  };

  const S = {
    page:        { background: t.pageBg, minHeight: '100vh', padding: '28px 24px 40px' } as React.CSSProperties,
    title:       { fontSize: 30, fontWeight: 900, color: t.titleColor, letterSpacing: -1, lineHeight: 1.1 } as React.CSSProperties,
    titleEm:     { color: '#dc2626', fontStyle: 'italic', fontFamily: 'Georgia, serif' } as React.CSSProperties,
    sub:         { fontSize: 13, color: t.subColor, marginTop: 6, fontWeight: 500 } as React.CSSProperties,
    card:        { background: t.cardBg, border: light ? 'none' : `1.5px solid ${t.cardBorder}`, borderRadius: 28, overflow: 'hidden' as const, marginBottom: 18, position: 'relative' as const, boxShadow: light ? '0 14px 36px rgba(190,30,90,0.14)' : '0 14px 36px rgba(0,0,0,0.45)' },
    banner:      { background: 'linear-gradient(90deg, #e11d48, #fb7185)', padding: '20px 22px', color: '#fff', fontSize: 19, fontWeight: 900, letterSpacing: -0.3, textAlign: 'center' as const },
    cardBody:    { padding: 22 } as React.CSSProperties,
    cardAccent:  { position: 'absolute' as const, top: 0, left: 0, right: 0, height: 3, background: '#dc2626', borderRadius: '18px 18px 0 0' },
    sectionLabel:{ fontSize: 10, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase' as const, color: t.labelColor, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 },
    fieldLabel:  { fontSize: 14, fontWeight: 800, color: t.titleColor, marginBottom: 8, display: 'block' } as React.CSSProperties,
    input:       { width: '100%', background: t.inputBg, border: `1.5px solid ${t.inputBorder}`, borderRadius: 16, padding: '16px 18px', fontSize: 16, color: t.inputColor, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' as const },
    submitBtn:   { width: '100%', background: 'linear-gradient(90deg, #e11d48, #ec4899)', color: '#fff', border: 'none', borderRadius: 999, padding: 22, fontSize: 18, fontWeight: 900, cursor: 'pointer', marginTop: 6, fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: '0 12px 28px rgba(225,29,72,0.38)' } as React.CSSProperties,
  };

  return (
    <div style={S.page}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        {/* Header */}
        <div style={{ marginBottom: 26 }}>
          <div style={S.title}>Start a <em style={S.titleEm}>Session</em></div>
          <div style={S.sub}>Pick who, pick where, ask away.</div>
        </div>

        {/* Partner Info Card */}
        <div style={S.card}>
          <div style={S.banner}>Who's it for?</div>
          <div style={S.cardBody}>
          <label style={S.fieldLabel}>Their name</label>
          <input
            style={S.input}
            placeholder="Their name or nickname"
            value={partner_name}
            onChange={e => setPartnerName(e.target.value)}
          />
          <div style={{ height: 14 }} />
          <label style={S.fieldLabel}>
            {isPhonePlatform ? 'Phone number' : 'Handle or username'}
          </label>

          {isPhonePlatform ? (
            /* ── Phone input with country code picker ── */
            <div ref={pickerRef}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
              {/* Country code — typeable search box */}
              <div style={{ position: 'relative', flexShrink: 0, width: showCodePicker ? 150 : 92 }}>
                <input
                  ref={anchorRef}
                  type="text"
                  value={showCodePicker ? codeSearch : `${selectedCountry.flag} ${selectedCountry.code}`}
                  onFocus={() => {
                    if (anchorRef.current) {
                      const rect = anchorRef.current.getBoundingClientRect();
                      const DROPDOWN_WIDTH = 260;
                      const VIEWPORT_MARGIN = 12;
                      const GAP = 6;
                      const desiredListHeight = 260;

                      const spaceBelow = window.innerHeight - rect.bottom - GAP - VIEWPORT_MARGIN;
                      const spaceAbove = rect.top - GAP - VIEWPORT_MARGIN;

                      // Prefer opening below; flip above if there's not enough room below
                      // but there is more room above. Otherwise, keep it below and shrink
                      // the list so it always stays fully on-screen and scrollable.
                      const openUp = spaceBelow < desiredListHeight && spaceAbove > spaceBelow;
                      const available = openUp ? spaceAbove : spaceBelow;
                      const maxListHeight = Math.max(140, Math.min(desiredListHeight, available));

                      const left = Math.min(
                        Math.max(VIEWPORT_MARGIN, rect.left),
                        window.innerWidth - DROPDOWN_WIDTH - VIEWPORT_MARGIN
                      );

                      setDropdownPos({
                        top: openUp ? rect.top - GAP : rect.bottom + GAP,
                        left,
                        maxListHeight,
                        openUp,
                      });
                    }
                    setShowCodePicker(true);
                    setCodeSearch('');
                  }}
                  onChange={e => setCodeSearch(e.target.value)}
                  placeholder="Search..."
                  style={{
                    width: '100%',
                    background: t.inputBg, border: `1.5px solid ${showCodePicker ? '#dc2626' : t.inputBorder}`,
                    borderRadius: 12, padding: '0 12px', fontSize: 14, color: t.inputColor,
                    cursor: 'text', fontFamily: 'inherit', fontWeight: 700,
                    transition: 'border-color 0.15s, width 0.15s',
                    outline: 'none', height: '100%',
                  }}
                />
              </div>

              {/* Number input */}
              <input
                style={{ ...S.input, flex: 1 }}
                placeholder="Phone number"
                value={rawNumber}
                onChange={e => {
  const cleaned = e.target.value.replace(/[^\d\s\-()]/g, '');
  setRawNumber(cleaned.startsWith('0') ? cleaned.slice(1) : cleaned);
}}
                type="tel"
                inputMode="numeric"
              />
            </div>

            {/* Dropdown — fixed positioned, renders above everything */}
            <AnimatePresence>
              {showCodePicker && (
                <motion.div
                  initial={{ opacity: 0, y: dropdownPos.openUp ? 8 : -8, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: dropdownPos.openUp ? 8 : -8, scale: 0.97 }}
                  transition={{ duration: 0.15 }}
                  style={{
                    position: 'fixed',
                    ...(dropdownPos.openUp
                      ? { bottom: window.innerHeight - dropdownPos.top, top: 'auto' }
                      : { top: dropdownPos.top }),
                    left: dropdownPos.left,
                    width: 260,
                    background: t.pickerBg,
                    border: `1.5px solid ${t.pickerBorder}`,
                    borderRadius: 14,
                    zIndex: 9999,
                    overflow: 'hidden',
                    boxShadow: '0 16px 40px rgba(0,0,0,0.4)',
                  }}
                >
                  {/* List */}
                  <div
                    style={{
                      maxHeight: dropdownPos.maxListHeight,
                      overflowY: 'auto',
                      WebkitOverflowScrolling: 'touch',
                      overscrollBehavior: 'contain',
                    }}
                  >
                    {filteredCodes.map(c => (
                      <button
                        key={c.name}
                        type="button"
                        onClick={() => { setCountryIso(c.iso); setShowCodePicker(false); }}
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                          padding: '10px 14px', background: countryIso === c.iso ? t.pickerHover : 'transparent',
                          border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                          borderBottom: `1px solid ${t.dividerColor}`,
                          transition: 'background 0.1s',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.background = t.pickerHover)}
                        onMouseLeave={e => (e.currentTarget.style.background = countryIso === c.iso ? t.pickerHover : 'transparent')}
                      >
                        <span style={{ fontSize: 18 }}>{c.flag}</span>
                        <span style={{ fontSize: 13, color: t.inputColor, fontWeight: 600, flex: 1, textAlign: 'left' }}>{c.name}</span>
                        <span style={{ fontSize: 12, color: t.mutedColor, fontWeight: 700 }}>{c.code}</span>
                      </button>
                    ))}
                    {filteredCodes.length === 0 && (
                      <div style={{ padding: '16px', textAlign: 'center', color: t.mutedColor, fontSize: 13 }}>No results</div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            </div>
          ) : (
            /* ── Handle input for non-phone platforms ── */
            <input
              style={{ ...S.input, marginBottom: 0 }}
              placeholder="@handle or username"
              value={partner_contact}
              onChange={e => setPartnerContact(e.target.value)}
            />
          )}

          {/* Preview assembled number */}
          {isPhonePlatform && rawNumber && (() => {
            const { contact, valid } = getContact();
            return (
              <div style={{ marginTop: 8, fontSize: 11, color: t.mutedColor, fontWeight: 600 }}>
                {valid
                  ? <>Will send to: <span style={{ color: '#dc2626' }}>{contact}</span></>
                  : <span style={{ color: '#dc2626' }}>That doesn't look like a valid {selectedCountry.name} number — double check it</span>}
              </div>
            );
          })()}
          </div>
        </div>

        {/* Platform Selector */}
        <div style={S.card}>
        <div style={S.banner}>Where should we send it?</div>
        <div style={S.cardBody}>
        <div
          role="radiogroup"
          aria-label="Where should we send this?"
          onKeyDown={onPlatformKeyDown}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(92px, 1fr))',
            gap: 10,
          }}>
          {PLATFORMS.map((plat, i) => {
            const selected = partner_platform === plat.name;
            return (
              <button
                key={plat.name}
                ref={el => { platformRefs.current[i] = el; }}
                type="button"
                role="radio"
                aria-checked={selected}
                tabIndex={selected ? 0 : -1}
                onClick={() => setPartnerPlatform(plat.name)}
                data-track={`create_platform_${plat.name}`}
                className="yhuu-platform-btn"
                data-selected={selected ? 'true' : 'false'}
                style={{
                  position: 'relative',
                  background: selected ? t.platActiveBg : t.platBg,
                  border: `1.5px solid ${selected ? '#dc2626' : t.platBorder}`,
                  boxShadow: selected ? '0 4px 14px rgba(220,38,38,.14)' : '0 1px 2px rgba(0,0,0,.04)',
                  borderRadius: 14,
                  padding: '14px 6px 12px',
                  minHeight: 88,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
                  cursor: 'pointer',
                  font: 'inherit',
                  WebkitTapHighlightColor: 'transparent',
                  transition: 'background .18s ease, border-color .18s ease, box-shadow .18s ease, transform .12s ease',
                }}>
                {selected && (
                  <span
                    aria-hidden="true"
                    style={{
                      position: 'absolute', top: 6, right: 6, width: 16, height: 16, borderRadius: '50%',
                      background: '#dc2626', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                    <Check size={10} strokeWidth={3.5} />
                  </span>
                )}
                <PlatformLogo platform={plat.name} active={selected} />
                <span style={{ fontSize: 11, fontWeight: 700, color: selected ? '#dc2626' : t.labelColor }}>
                  {plat.name}
                </span>
              </button>
            );
          })}
        </div>
        </div>
        </div>

        {/* Questions */}
        <div style={S.card}>
          <div style={S.banner}>What do you want to ask?</div>
          <div style={S.cardBody}>

          <AnimatePresence initial={false}>
            {questions.map((q, index) => (
              <motion.div key={q.id} initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.98 }}
                style={{ background: light ? '#fff7fa' : '#0d0d10', border: `1.5px solid ${t.qCardBorder}`, borderRadius: 20, padding: '16px', display: 'flex', gap: 12, marginBottom: 12 }}>
                <div style={{ width: 28, height: 28, borderRadius: 8, background: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 900, color: '#fff', flexShrink: 0 }}>
                  {String(index + 1).padStart(2, '0')}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: t.titleColor, marginBottom: 6 }}>Your question</div>
                  <input
                    style={{ width: '100%', background: 'transparent', border: 'none', borderBottom: `1.5px solid ${t.dividerColor}`, padding: '8px 0', fontSize: 16, color: t.inputColor, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                    placeholder="Enter your question here..."
                    value={q.text}
                    onChange={e => updateQuestion(q.id, e.target.value)}
                    onFocus={e => { (e.target as HTMLInputElement).style.borderBottomColor = '#dc2626'; }}
                    onBlur={e => { (e.target as HTMLInputElement).style.borderBottomColor = t.dividerColor; }}
                  />

                  <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button type="button" onClick={() => setBuddyFor({ id: q.id, mode: 'chat' })}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 800, padding: '8px 12px', borderRadius: 999, border: '1.5px solid #dc2626', background: 'transparent', color: '#dc2626', cursor: 'pointer', fontFamily: 'inherit' }}>
                      <Sparkles size={13} /> Need help deciding?
                    </button>
                    {!q.audioUrl && (
                      <button type="button" onClick={() => setBuddyFor({ id: q.id, mode: 'voice' })}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 800, padding: '8px 12px', borderRadius: 999, border: `1.5px solid ${t.dividerColor}`, background: 'transparent', color: t.titleColor, cursor: 'pointer', fontFamily: 'inherit' }}>
                        <Volume2 size={13} /> Text to voice
                      </button>
                    )}
                  </div>

                  <div style={{ marginTop: 10 }}>
                    {q.audioUrl ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <audio src={q.audioUrl} controls style={{ height: 32, flex: '1 1 0', minWidth: 0, width: '100%', maxWidth: '100%' }} />
                        <button type="button" onClick={() => clearQuestionAudio(q.id)}
                          aria-label="Remove voice note"
                          style={{ flexShrink: 0, whiteSpace: 'nowrap', fontSize: 11, fontWeight: 800, color: '#dc2626', background: 'transparent', border: '1.5px solid #dc2626', borderRadius: 999, padding: '6px 10px', cursor: 'pointer', fontFamily: 'inherit' }}>
                          Remove
                        </button>
                      </div>
                    ) : partner_platform === 'WhatsApp' ? (
                      <VoiceRecorder pathPrefix={`questions/${q.id}`} onRecorded={(url) => updateQuestionAudio(q.id, url)} disguise />
                    ) : null}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: t.titleColor, marginBottom: 6, marginTop: 16 }}>What do you expect them to say? <span style={{ color: t.mutedColor, fontWeight: 600, whiteSpace: 'nowrap' }}>(optional)</span></div>
                  <input
                    style={{ width: '100%', background: 'transparent', border: 'none', borderBottom: `1.5px solid ${t.dividerColor}`, padding: '8px 0', fontSize: 16, color: t.mutedColor, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                    placeholder="Your gut feeling..."
                    value={q.expectedAnswer}
                    onChange={e => updateExpected(q.id, e.target.value)}
                    onFocus={e => { (e.target as HTMLInputElement).style.borderBottomColor = '#dc2626'; (e.target as HTMLInputElement).style.color = t.inputColor; }}
                    onBlur={e => { (e.target as HTMLInputElement).style.borderBottomColor = t.dividerColor; (e.target as HTMLInputElement).style.color = t.mutedColor; }}
                  />
                </div>
                {questions.length > 1 && (
                  <button type="button" onClick={() => removeQuestion(q.id)}
                    style={{ color: t.dividerColor, fontSize: 15, cursor: 'pointer', background: 'none', border: 'none', paddingTop: 4, flexShrink: 0 }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#dc2626')}
                    onMouseLeave={e => (e.currentTarget.style.color = t.dividerColor)}>
                    <Trash2 size={15} />
                  </button>
                )}
              </motion.div>
            ))}
          </AnimatePresence>

        <button
          type="button"
          onClick={addQuestion}
            data-track="create_add_question"
            style={{
              width: '100%', marginTop: 4, padding: '16px', borderRadius: 999,
              background: t.addBtnBg, border: `2px dashed ${t.addBtnBorder}`,
              color: '#e11d48', fontSize: 15, fontWeight: 800, fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer',
            }}
          >
          <Plus size={18} strokeWidth={3} /> Add another question
        </button>
          </div>
        </div>

        <div
          style={{
            ...S.card,
            marginTop: 14,
            border: liveAiEnabled ? '1.5px solid #a855f7' : `1.5px solid ${t.cardBorder}`,
            background: liveAiEnabled ? (light ? '#faf5ff' : '#171022') : t.cardBg,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: '#a855f7', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900 }}>AI</div>
            <div style={{ flex: 1 }}>
              <div style={{ color: t.titleColor, fontWeight: 900, fontSize: 14 }}>Live Yhuu AI <span style={{ color: '#a855f7', fontSize: 10, letterSpacing: 1 }}>OPTIONAL</span></div>
              <p style={{ color: t.mutedColor, fontSize: 12, lineHeight: 1.5, marginTop: 4 }}>
                Yhuu AI will greet the recipient, play or read your question, listen to their approved answer, and save it to this same session. Your existing admin dispatch and link stay unchanged.
              </p>
              <label style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 12, color: t.titleColor, fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
                <input type="checkbox" checked={liveAiEnabled} onChange={e => setLiveAiEnabled(e.target.checked)} style={{ width: 18, height: 18, accentColor: '#a855f7' }} />
                Enable Live Yhuu AI for this session
              </label>
            </div>
          </div>
        </div>

        <button style={S.submitBtn} onClick={handleSubmit} disabled={isSubmitting} data-track="create_send_click">
          {isSubmitting ? (
            <div style={{ width: 20, height: 20, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          ) : 'Send It Anonymously'}
        </button>
      </motion.div>

      {showAuthGate && <AuthGateModal onClose={() => setShowAuthGate(false)} />}
      {buddyFor && (
        <QuestionBuddy
          light={light}
          partnerName={partner_name}
          platform={partner_platform}
          existingQuestions={questions.map(q => q.text).filter(Boolean)}
          signedIn={!!user}
          canVoice
          questionId={buddyFor.id}
          initialText={questions.find(q => q.id === buddyFor.id)?.text ?? ''}
          initialMode={buddyFor.mode}
          onUseText={(text) => {
            setQuestions(qs => qs.map(q => q.id === buddyFor.id ? { ...q, text } : q));
            setBuddyFor(null);
          }}
          onAttachVoice={(audioUrl, text) => {
            // one update for both fields — two separate setters would overwrite each other
            setQuestions(qs => qs.map(q => q.id === buddyFor.id ? { ...q, text, audioUrl } : q));
            setBuddyFor(null);
          }}
          onClose={() => setBuddyFor(null)}
        />
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        input::placeholder { color: ${t.mutedColor}; }
      `}</style>
    </div>
  );
}
